import React from "react";
import { Col, Form, Input, Modal, Select, Switch } from "antd";
import { EyeOutlined, DownloadOutlined } from "@ant-design/icons";
import useCartStore from "../../../../stores/useCartStore";
import { toCamelCase, normalizeStaticSelectValue, getStaticDisplayValue } from "./customerFormUtils";
import useDocumentViewer from "./useDocumentViewer";

/**
 * Renders a single dynamic customer form field (text/select/date/yesno)
 * plus optional file-upload buttons for PAN and Passport.
 */
const CustomerFormField = ({
  field,
  group,
  activeTabName,
  isViewMode,
  isPanCustomer,
  gstInfo,
  selectedB2BCustomer,
  isNriBilling,
  isCreditSale,
  isPanUpdateMode,
  panCustomerInfo,
  formDataCache,
  activeTab,
  customerForm,
  optionsMap,
  handleChange,
  handleApiEnterValidation,
  handleFileUpload,
  fetchOptions,
  documents,
  setDocuments,
  selectedEditOldCustomer,
  hidden = false,
  extra = null,
  className = "",
}) => {
  const { openDocument, docViewer, closeDocViewer } = useDocumentViewer();

  const fieldName = field.name?.toLowerCase();
  const isPassportField = fieldName?.startsWith("passport");
  const isVisaField = fieldName?.startsWith("visa");
  const isImmigrationField = fieldName?.startsWith("immigration");
  const isNriMandatoryField = isNriBilling && (isPassportField || isVisaField || isImmigrationField);
  // BUG-2380: honour the admin's per-field "Read Only" from the customer
  // configuration (field.readOnly). It locks the field for typing in every flow;
  // values can still arrive programmatically -- e.g. GST validation fills GST No
  // through customerForm.setFieldsValue (B2BCustomerSearch.js) -- so an
  // unvalidated GSTIN can no longer be typed in and saved without its address.
  const isConfigReadOnly = field.readOnly === true;
  const isFieldDisabled = isViewMode || isConfigReadOnly;

  // Detects fields where the admin stored a JSON object of static options
  // (e.g., {"COM": "Composite", "UNR": "Unregistered"}) inside the defaultValue.
  // KWC config has Gst Type (type=select) and Company Type (type=text) like this.
  // We treat any such field as a select dropdown regardless of declared type.
  const parseStaticOptions = (f) => {
    if (f?.dataSource?.type !== "static" || !f?.defaultValue) return null;
    try {
      const parsed = JSON.parse(f.defaultValue);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed) && Object.keys(parsed).length > 0) {
        return Object.entries(parsed).map(([key, value]) => ({ key, value }));
      }
    } catch (err) {
      console.error("[CustomerFormField] Failed to parse static options from defaultValue:", err);
    }
    return null;
  };
  const staticOptionsFromDefault = parseStaticOptions(field);
  const isStaticOptionsField = !!staticOptionsFromDefault;

  const renderInput = () => {
    // If field has a JSON-of-options defaultValue, render as Select even if
    // declared type is text — fixes Gst Type / Company Type showing raw JSON.
    if (isStaticOptionsField) {
      let selectValue =
        formDataCache?.[activeTab]?.[group.groupId]?.[field.name] ??
        customerForm.getFieldValue(group.groupId ? [group.groupId, toCamelCase(field.name)] : toCamelCase(field.name)) ??
        "";
      // Reverse-map display value (e.g., "Composite") back to key (e.g., "COM") if needed.
      try {
        const parsed = JSON.parse(field.defaultValue);
        const reverseMap = Object.fromEntries(Object.entries(parsed).map(([key, value]) => [value, key]));
        if (reverseMap[selectValue]) selectValue = reverseMap[selectValue];
      } catch (err) {
        console.error("[CustomerFormField] Failed to reverse-map select value:", err);
      }
      return (
        <Select
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
          value={selectValue}
          className="custom-select"
          dropdownStyle={{ maxHeight: 150, overflowY: "auto" }}
          required={field.mandatory}
          disabled={isFieldDisabled}
          style={{
            width: "100%",
            paddingLeft: "8px",
            borderRadius: "4px",
            height: "7vh",
            border: "1px solid #d9d9d9",
            marginTop: "10px",
          }}
          onChange={(e) => handleChange(e, field, group.groupId)}
        >
          <option value="">Select</option>
          {staticOptionsFromDefault.map((opt) => (
            <option key={opt.key} value={opt.key}>
              {opt.value}
            </option>
          ))}
        </Select>
      );
    }

    if (field.type === "text" || field.type === "textarea" || field.type === "email" || field.type === "number" || field.type === "phone") {
      const isGstField = field.name?.toLowerCase().includes("gst");
      const isGstLocked = isGstField && (!!gstInfo || !!selectedB2BCustomer);
      const normalizedFieldName = field.name?.toLowerCase().replace(/\s+/g, "");
      const isCompanyField = normalizedFieldName === "companyname" || normalizedFieldName?.includes("companyname");
      const isCompanyLocked = isCompanyField && (!!gstInfo || !!selectedB2BCustomer);
      const isMobileField = normalizedFieldName.startsWith("mobile") || normalizedFieldName.startsWith("phone");
      const isPanMobileReadonly = isPanCustomer === true && panCustomerInfo?.panHasMobile === true && isMobileField;
      const isPanField = normalizedFieldName === "pancard" || normalizedFieldName === "panno" || normalizedFieldName === "pannumber";
      const isPanLocked = isPanField && panCustomerInfo?.isValid === true;
      const fieldPath = group.groupId ? [String(group.groupId).toLowerCase(), toCamelCase(field.name)] : toCamelCase(field.name);
      const gstValue = customerForm.getFieldValue(fieldPath);
      const isReadOnly = isGstLocked || isCompanyLocked || isPanMobileReadonly || isPanLocked || isFieldDisabled;

      return (
        <Input
          type={field.type === "phone" ? "tel" : field.type}
          placeholder={field.placeholder}
          value={gstValue}
          readOnly={isReadOnly}
          disabled={isReadOnly}
          required={field.mandatory}
          pattern={field.validation || undefined}
          onInput={
            field.type === "phone" || field.type === "number"
              ? (e) => {
                  if (!isReadOnly) {
                    // Phone fields may start with a country code (e.g. +971); keep a
                    // leading "+" but strip it (and everything else non-digit) elsewhere.
                    e.target.value =
                      field.type === "phone" ? e.target.value.replace(/(?!^\+)[^\d]/g, "") : e.target.value.replace(/\D/g, "");
                  }
                }
              : undefined
          }
          onChange={(e) => {
            if (!isReadOnly) {
              handleChange(e, field, group.groupId);
            }
          }}
          onKeyDown={(e) => !isFieldDisabled && handleApiEnterValidation(e, field)}
          style={{
            width: "100%",
            paddingLeft: "8px",
            borderRadius: "4px",
            height: "7vh",
            border: "1px solid #d9d9d9",
            marginTop: "10px",
            backgroundColor: isReadOnly ? "#f5f5f5" : "#fff",
            cursor: isReadOnly ? "not-allowed" : "text",
          }}
        />
      );
    }

    if (field.type === "select") {
      let staticOptions = [];
      if (field.dataSource?.type === "static" && field.defaultValue) {
        try {
          const parsed = JSON.parse(field.defaultValue);
          staticOptions = Object.entries(parsed).map(([key, value]) => ({ key, value }));
        } catch (err) {
          console.error("[CustomerFormField] Failed to parse static select options:", err);
        }
      }
      const dynamicOptions = optionsMap[field.id] || [];
      let selectValue = formDataCache?.[activeTab]?.[group.groupId]?.[field.name] ?? customerForm.getFieldValue(group.groupId ? [group.groupId, field.name] : field.name) ?? "";

      if (field.dataSource?.type === "static" && field.defaultValue) {
        try {
          const parsed = JSON.parse(field.defaultValue);
          const reverseMap = Object.fromEntries(Object.entries(parsed).map(([key, value]) => [value, key]));
          if (reverseMap[selectValue]) {
            selectValue = reverseMap[selectValue];
          }
        } catch (err) {
          console.error("[CustomerFormField] Failed to reverse-map select value:", err);
        }
      }

      return (
        <Select
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
          value={selectValue}
          className="custom-select"
          dropdownStyle={{ maxHeight: 150, overflowY: "auto" }}
          required={field.mandatory}
          disabled={isFieldDisabled}
          style={{
            width: "100%",
            paddingLeft: "8px",
            borderRadius: "4px",
            height: "7vh",
            border: "1px solid #d9d9d9",
            marginTop: "10px",
          }}
          onClick={() => {
            if (!isFieldDisabled && field.dataSource?.type !== "static") {
              fetchOptions(field);
            }
          }}
          onChange={(e) => handleChange(e, field, group.groupId)}
        >
          <option value="">Select</option>
          {staticOptions.length > 0 &&
            staticOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.value}
              </option>
            ))}
          {staticOptions.length === 0 &&
            dynamicOptions.map((opt) => (
              <option key={opt.RecordID || opt.Name} value={opt.RecordID}>
                {opt.Name}
              </option>
            ))}
        </Select>
      );
    }

    if (field.type === "date") {
      return (
        <Input
          type="date"
          defaultValue={field.defaultValue}
          required={field.mandatory}
          disabled={isFieldDisabled}
          onChange={(e) => handleChange(e, field, group.groupId)}
          style={{
            width: "100%",
            paddingLeft: "8px",
            borderRadius: "4px",
            height: "7vh",
            border: "1px solid #d9d9d9",
            marginTop: "10px",
          }}
        />
      );
    }

    if (field.type === "yesno") {
      return (
        <Switch
          checked={group.groupId ? formDataCache?.[activeTab]?.[group.groupId]?.[field.name] === true : formDataCache?.[activeTab]?.[field.name] === true}
          disabled={isFieldDisabled}
          onChange={(checked) => handleChange({ target: { value: checked } }, field, group.groupId)}
        />
      );
    }

    return null;
  };

  const creditLimitInitialValue = (() => {
    const fn = field.name?.toLowerCase().replace(/\s+/g, "");
    const isCreditLimitField = fn.startsWith("credit");
    if (activeTabName?.toLowerCase() === "contact" && isCreditLimitField && isCreditSale && !isPanUpdateMode) {
      return 1;
    }
    // For static-options fields (Gst Type / Company Type), the defaultValue
    // holds the JSON list of options, NOT a real default. Don't seed the form
    // with the raw JSON — leave it empty so the Select shows "Select" placeholder.
    if (isStaticOptionsField) return "";
    return getStaticDisplayValue(field, field.defaultValue);
  })();

  return (
    <Col span={12} key={field.id} className={[className, hidden ? "cwc-tucked" : ""].filter(Boolean).join(" ") || undefined}>
      <div
        style={{
          borderRadius: "6px",
          paddingRight: "0px",
          ...(isNriMandatoryField ? { backgroundColor: "#FFF7E0", padding: "8px", border: "1px solid #F8C519", borderRadius: "8px", marginBottom: "4px" } : {}),
        }}
      >
        <label style={{ fontWeight: 600, fontSize: "1.1em", color: "#0F0718" }}>
          {field.name} {(field.mandatory || isNriMandatoryField) && <span style={{ color: "red", fontWeight: 700 }}> *</span>}
          {isNriMandatoryField && <span style={{ fontSize: "0.75em", color: "#B45309", marginLeft: "6px" }}>(Required for NRI)</span>}
        </label>
        <Form.Item
          name={group.groupId ? [String(group.groupId).toLowerCase(), toCamelCase(field.name)] : toCamelCase(field.name)}
          key={field.id}
          rules={[
            { required: field.mandatory || isNriMandatoryField, message: `${field.name} is required` },
            ...(field.validation
              ? [
                  {
                    pattern:
                      isNriBilling && (field.name?.toLowerCase().replace(/\s+/g, "").startsWith("mobile") || field.name?.toLowerCase().replace(/\s+/g, "").startsWith("phone"))
                        ? undefined
                        : new RegExp(field.validation),
                    message: `Please enter a valid ${field.name}`,
                  },
                ]
              : []),
          ]}
          initialValue={creditLimitInitialValue}
        >
          {renderInput()}
        </Form.Item>
        {extra}

        {isPanCustomer && field.name.toLowerCase().startsWith("pan") && (
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <label style={{ fontWeight: 500, fontSize: "0.95em", color: "#0F0718", minWidth: "140px" }}>Upload PAN Card</label>
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              disabled={isViewMode}
              style={{ flex: 1, height: "30px", fontSize: "0.95em" }}
              onChange={async (e) => {
                const file = e.target.files[0];
                const success = await handleFileUpload(e, "PAN Card");
                if (success) {
                  customerForm.setFieldsValue({ contact: { panCardFile: file } });
                } else {
                  e.target.value = "";
                }
              }}
            />
            {(() => {
              const cartCustomer = useCartStore.getState().cart?.customer;
              // Only fall back to the cart customer's stored PAN when editing an
              // existing customer. In the add-new-customer flow (selectedEditOldCustomer
              // is null) we must never surface another customer's document.
              const panUrl = selectedEditOldCustomer ? cartCustomer?.metaData?.find((m) => m.key === "PAN_CARD_URL")?.value : undefined;
              const panDoc = documents.find((doc) => doc.docName === "PAN Card");
              const displayUrl = panDoc?.docUrl || panUrl;
              const panExistingCustomerUrl =
                selectedEditOldCustomer?.metaData?.find((m) => m.key === "PAN_CARD_URL") || selectedEditOldCustomer?.metaData?.find((m) => m.key === "P ANCARDURL");
              const panDocFromCustomer = panExistingCustomerUrl?.value ? { fileName: panExistingCustomerUrl.value.split("/").pop(), docUrl: panExistingCustomerUrl.value } : null;
              const finalPanUrl = panDocFromCustomer?.fileName || null;
              if (!displayUrl) return null;
              return (
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <span style={{ fontSize: "0.8em", color: "#16a34a", fontWeight: 600 }}>Uploaded</span>
                  <span> {panDoc?.fileName || finalPanUrl}</span>
                  <a
                    href={displayUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="View PAN document"
                    style={{ color: "#2F3856", cursor: "pointer" }}
                    onClick={(e) => { e.preventDefault(); openDocument(displayUrl); }}
                  >
                    <EyeOutlined style={{ fontSize: "18px" }} /> View
                  </a>
                  <a
                    href={displayUrl}
                    download
                    title="Download PAN document"
                    style={{ color: "#2F3856", cursor: "pointer" }}
                    onClick={(e) => {
                      e.preventDefault();
                      if (window.electronAPI?.shell?.downloadFile) {
                        window.electronAPI.shell.downloadFile(displayUrl);
                      } else {
                        fetch(displayUrl)
                          .then((res) => res.blob())
                          .then((blob) => {
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = displayUrl.split("/").pop() || "PAN_document";
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          })
                          .catch(() => window.open(displayUrl, "_blank"));
                      }
                    }}
                  >
                    <DownloadOutlined style={{ fontSize: "18px" }} /> Download
                  </a>
                </div>
              );
            })()}
          </div>
        )}

        {isNriBilling && field.name.toLowerCase().startsWith("passport") && (
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <label style={{ fontWeight: 500, fontSize: "0.95em", color: "#0F0718", minWidth: "140px" }}>Upload Passport</label>
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              disabled={isViewMode}
              style={{ flex: 1, height: "30px", fontSize: "0.95em" }}
              onChange={async (e) => {
                const file = e.target.files[0];
                const success = await handleFileUpload(e, "Passport");
                if (success) {
                  customerForm.setFieldsValue({ contact: { passportFile: file } });
                } else {
                  e.target.value = "";
                }
              }}
            />
            {(() => {
              const cartCustomer = useCartStore.getState().cart?.customer;
              // Only fall back to the cart customer's stored passport when editing an
              // existing customer, never in the add-new-customer flow.
              const passportUrl = selectedEditOldCustomer ? cartCustomer?.metaData?.find((m) => m.key === "PASSPORT_URL")?.value : undefined;
              const passportDoc = documents.find((doc) => doc.docName === "Passport");
              const displayUrl = passportDoc?.docUrl || passportUrl;
              if (!displayUrl) return null;
              return (
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <span style={{ fontSize: "0.8em", color: "#16a34a", fontWeight: 600 }}>Uploaded</span>
                  <span> {passportDoc?.fileName}</span>
                  <a
                    href={displayUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="View Passport document"
                    style={{ color: "#2F3856", cursor: "pointer" }}
                    onClick={(e) => { e.preventDefault(); openDocument(displayUrl); }}
                  >
                    <EyeOutlined style={{ fontSize: "18px" }} /> View
                  </a>
                  <a
                    href={displayUrl}
                    download
                    title="Download Passport document"
                    style={{ color: "#2F3856", cursor: "pointer" }}
                    onClick={(e) => {
                      e.preventDefault();
                      if (window.electronAPI?.shell?.downloadFile) {
                        window.electronAPI.shell.downloadFile(displayUrl);
                      } else {
                        fetch(displayUrl)
                          .then((res) => res.blob())
                          .then((blob) => {
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = displayUrl.split("/").pop() || "Passport_document";
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          })
                          .catch(() => window.open(displayUrl, "_blank"));
                      }
                    }}
                  >
                    <DownloadOutlined style={{ fontSize: "18px" }} /> Download
                  </a>
                </div>
              );
            })()}
          </div>
        )}
      </div>
      <Modal
        open={docViewer.visible}
        onCancel={closeDocViewer}
        footer={null}
        width="85vw"
        style={{ top: 20 }}
        title="Document Preview"
        destroyOnClose
      >
        {docViewer.url && (
          docViewer.isPdf ? (
            <iframe
              src={docViewer.url}
              title="Document"
              style={{ width: "100%", height: "75vh", border: "none" }}
            />
          ) : (
            <img
              src={docViewer.url}
              alt="Document"
              style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain", display: "block", margin: "0 auto" }}
            />
          )
        )}
      </Modal>
    </Col>
  );
};

export default CustomerFormField;
